import { supabase } from '@/lib/supabase'
import type {
  FriendRequest,
  FriendState,
  FriendSummary,
  PersonSearchResult,
  ProfileRow,
} from '@/types/database'

/**
 * Search is a single RPC on purpose: the privacy rules (exact-phone only,
 * respect the discoverability switches, hide blocked pairs) belong in the
 * database where a hand-rolled client query cannot route around them.
 */
export async function searchPeople(query: string): Promise<PersonSearchResult[]> {
  const q = query.trim()
  if (q.length < 2) return []
  const { data, error } = await supabase.rpc('search_people', { q, max_rows: 20 })
  if (error) throw error
  return data ?? []
}

export async function getProfile(userId: string): Promise<ProfileRow | null> {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle()
  if (error) throw error
  return data
}

/**
 * Friendship is mutual and consented, so every write is an RPC rather than a
 * row the client inserts: the pair has to be stored in a canonical order, and
 * the rules about who may accept are not something a client should be trusted
 * to apply. See `supabase/migrations/*_friendships.sql`.
 */
export async function friendState(otherId: string): Promise<FriendState> {
  const { data, error } = await supabase.rpc('friend_state', { other: otherId })
  if (error) throw error
  return data ?? 'none'
}

/** Returns where you now stand — asking someone who already asked you accepts. */
export async function sendFriendRequest(targetId: string): Promise<FriendState> {
  const { data, error } = await supabase.rpc('send_friend_request', { target: targetId })
  if (error) throw error
  return data ?? 'outgoing'
}

export async function acceptFriendRequest(requesterId: string) {
  const { error } = await supabase.rpc('accept_friend_request', { requester: requesterId })
  if (error) throw error
}

/** Decline, cancel and unfriend are one operation: drop the edge. */
export async function removeFriend(otherId: string) {
  const { error } = await supabase.rpc('remove_friend', { other: otherId })
  if (error) throw error
}

export async function listFriendRequests(): Promise<FriendRequest[]> {
  const { data, error } = await supabase.rpc('list_friend_requests')
  if (error) throw error
  return data ?? []
}

export async function listFriends(): Promise<FriendSummary[]> {
  const { data, error } = await supabase.rpc('list_friends')
  if (error) throw error
  return data ?? []
}

/** Friends you and this person share -- meaningless for your own profile. */
export async function mutualFriendCount(otherId: string): Promise<number> {
  const { data, error } = await supabase.rpc('mutual_friend_count', { other: otherId })
  if (error) throw error
  return data ?? 0
}

export async function blockUser(blockedId: string, blockerId: string) {
  const { error } = await supabase
    .from('blocks')
    .insert({ blocker_id: blockerId, blocked_id: blockedId })
  if (error && error.code !== '23505') throw error
}

export async function unblockUser(blockedId: string, blockerId: string) {
  await supabase.from('blocks').delete().eq('blocker_id', blockerId).eq('blocked_id', blockedId)
}

export async function updateProfile(userId: string, patch: Partial<ProfileRow>) {
  const { data, error } = await supabase
    .from('profiles')
    .update(patch)
    .eq('id', userId)
    .select('*')
    .single()
  if (error) throw error
  return data
}

export async function uploadAvatar(userId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'jpg'
  const path = `${userId}/avatar.${ext}`
  const { error } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: true, contentType: file.type, cacheControl: '3600' })
  if (error) throw error
  const { data } = supabase.storage.from('avatars').getPublicUrl(path)
  // Bust the CDN cache so the new avatar shows up immediately.
  return `${data.publicUrl}?v=${Date.now()}`
}

/**
 * Blocked people, via RPC: RLS hides the profiles of anyone you have blocked,
 * so a plain join returns ids with no names attached and the list is unusable.
 */
export async function listBlocked() {
  const { data, error } = await supabase.rpc('list_blocked')
  if (error) throw error
  return data ?? []
}
